/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef } from 'react';

// Tipagem para o Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: 'no-speech' | 'audio-capture' | 'not-allowed' | 'network' | 'aborted' | 'language-not-supported';
}

// Interface para o Web Speech API
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  start(): void;
  stop(): void;
}

interface VoiceControlProps {
  setMessage: React.Dispatch<React.SetStateAction<string>>;
}

interface VoiceControlReturn {
  isVoiceOn: boolean;
  isListening: boolean;
  handleStartListening: () => void;
  stopListening: () => void;
  toggleVoice: () => void;
}

export const useVoiceControl = ({ setMessage }: VoiceControlProps): VoiceControlReturn => {
  const [isVoiceOn, setIsVoiceOn] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 segundo entre tentativas

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Erro ao parar reconhecimento:', e);
      }
    }
    setIsListening(false);
    retryCountRef.current = 0;
  }, []);

  const handleStartListening = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || 
                             (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('Reconhecimento de voz não suportado neste navegador');
      return;
    }

    stopListening(); // Para qualquer reconhecimento anterior

    const recognition = new SpeechRecognition() as SpeechRecognition;
    recognitionRef.current = recognition;
    
    recognition.lang = 'pt-BR';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('Iniciando reconhecimento de voz...');
      setIsListening(true);
      retryCountRef.current = 0;
    };

  recognition.onresult = (event: SpeechRecognitionEvent) => {
  console.log('Resultado do reconhecimento no evento: recognition.onresult', event);
  const results = Array.from(event.results);
  
  for (let i = event.resultIndex; i < results.length; i++) {
    const result = results[i];
    if (result.isFinal) {
      const transcript = result[0].transcript.trim();
      console.log('Texto reconhecido:', transcript);
      
      // Lista de comandos de envio
      const sendCommands = [
        'enviar agora',
        'enviar mensagem',
        'envie agora',
        'mandar mensagem',
        'encaminhar mensagem',
        'mandar agora',
        'enviando agora',
        'pode enviar',
        'envie por favor',
        'mande agora',
        'enviar isso',
        'mandar isso'
      ];

      // Verifica se é um comando de envio
      const normalizedText = transcript.toLowerCase();
      if (sendCommands.some(cmd => normalizedText.includes(cmd))) {
        console.log('Comando de envio detectado:', transcript);
        const event = new CustomEvent('voiceCommand', { 
          detail: { type: 'send' } 
        });
        window.dispatchEvent(event);
      } else {
        // Se não for comando, atualiza o texto no input
        setMessage((previousText: string) => {
          const newText = previousText ? `${previousText} ${transcript}` : transcript;
          console.log('Texto atual:', previousText);
          console.log('Novo texto:', transcript);
          console.log('Texto concatenado:', newText);
          return newText;
        });
      }
     }
   }
  };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const errorMessages: Record<string, string> = {
        'no-speech': 'Nenhuma fala detectada. Por favor, fale algo.',
        'audio-capture': 'Não foi possível acessar o microfone.',
        'not-allowed': 'Permissão para usar o microfone foi negada.',
        'network': 'Erro de rede ao processar a fala.',
        'aborted': 'Reconhecimento de voz interrompido.',
        'language-not-supported': 'Idioma não suportado.'
      };

      console.error('Erro no reconhecimento:', errorMessages[event.error]);
      
      // Tenta reiniciar apenas para erros de rede
      if (event.error === 'network' && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        console.log(`Tentativa ${retryCountRef.current} de ${MAX_RETRIES}`);
        
        setTimeout(() => {
          try {
            stopListening();
            handleStartListening();
          } catch (e) {
            console.error('Erro ao reiniciar após falha:', e);
            stopListening();
          }
        }, RETRY_DELAY * retryCountRef.current); // Aumenta o delay a cada tentativa
      } else if (event.error === 'not-allowed') {
        console.log('Por favor, permita o acesso ao microfone para usar o reconhecimento de voz.');
        stopListening();
      } else {
        stopListening();
      }
    };

    recognition.onend = () => {
      console.log('Reconhecimento finalizado');
      if (isListening && retryCountRef.current < MAX_RETRIES) {
        try {
          recognition.start();
        } catch (e) {
          console.error('Erro ao reiniciar reconhecimento:', e);
          stopListening();
        }
      } else {
        stopListening();
      }
    };

    try {
      recognition.start();
    } catch (e) {
      console.error('Erro ao iniciar reconhecimento:', e);
      stopListening();
    }
  }, [isListening, stopListening, setMessage]);

  const toggleVoice = useCallback(() => {
    setIsVoiceOn((prev) => !prev);
  }, []);

  return {
    isVoiceOn,
    isListening,
    handleStartListening,
    stopListening,
    toggleVoice,
  };
};